import React, {createRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import * as ReactDOM from 'react-dom'
import {uid} from 'react-uid/dist/es5'

import GeoReply from './GeoReply'
import QuickReply from './QuickReply/QuickReply'
import {
  NextIcon,
  PrevIcon
} from '../../../icons/Icons'

import { VERTICAL } from '../../../constants/string'
import {hasPayloadValue} from '../../../helper/paramUtil'
import classes from './styles.module.scss'
import classNames from 'classnames'

const additionalMargin = 8

const QuickReplyList = ({
  message,
  replies,
  postMessage,
  settings,
  isMaximized,
  isImageLoaded,
  setIsImageLoaded,
  isLoadingIframeData,
  setPassedOrSkippedFlowComponentsId,
  setSelectedValue,
  setRating
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [leftScrollValue, setLeftScrollValue] = useState(0)
  const [showScroll, setShowScroll] = useState(false)
  const [showReplies, setShowReplies] = useState(true)
  const [showAnimation, setShowAnimation] = useState(false)
  const [hideRepliesTimeout, setHideRepliesTimeout] = useState(null)

  const isVerticalPosition = settings.quickReplyPosition === VERTICAL.toUpperCase()
  const hasDropdown = !!message?.dropdown
  const hasRating = !!message?.rating

  const _wrapper = useRef(null)
  const replyList = useRef(null)

  const refs = useMemo(() => {
    if (replies.length) {
      return replies.map((reply, index) => `reply${index}`)
    } else {
      return []
    }
  }, [replies])

  const refsArray = useMemo(() => refs.map(() => createRef()), [refs])

  const wrapperStyles = useMemo(() => {
    const styles = {}
    if (!showReplies) {
      styles.visibility = 'hidden'
    }
    if (showAnimation) {
      styles.animation = '.7s fadeOut'
    }
    return styles
  }, [showReplies, showAnimation])

  const isTextReply = useCallback(reply => reply.type && reply.type === 'text', [])

  const checkIfScrollShouldBeShowed = useCallback(() => {
    return replyList.current && replyList.current.scrollWidth - replyList.current.offsetWidth > leftScrollValue
  }, [replyList, leftScrollValue])

  const isLeftArrowNeeded = useMemo(() => {
    return replies && currentIndex > 0 && replyList && replyList.current && leftScrollValue > 0
  }, [replies, replyList, leftScrollValue])

  const isRightArrowNeeded = useMemo(() => {
    return replies
      && (currentIndex < replies.length)
      && (replies.length > 1)
      && (checkIfScrollShouldBeShowed() || leftScrollValue === 0)
      && showScroll
  }, [replyList, showScroll, leftScrollValue])

  const quickRepliesHolderStyles = useMemo(() => {
    const condition = isRightArrowNeeded || isLeftArrowNeeded
    return {
      justifyContent: condition ? 'left' : 'center',
      listStyleType: 'none',
      margin: 0,
      padding: 0
    }
  }, [isLeftArrowNeeded, isRightArrowNeeded])

  useEffect(() => {
    if (replies.length) {
      setShowScroll(scrollCounter())
      setCurrentIndex(0)
      setLeftScrollValue(0)
      replyList.current.scrollLeft = 0
    }

    return () => clearTimeout(hideRepliesTimeout)
  }, [replyList, isMaximized])

  const nextItem = () => scrollItems(true)
  const prevItem = () => scrollItems(false)

  const scrollItems = next => {
    const index = !next ? currentIndex - 1 : currentIndex
    const scrollValue = ReactDOM.findDOMNode(refsArray[index].current).getBoundingClientRect().width

    let countedScrollValue = leftScrollValue + (next ? scrollValue : -scrollValue)
    const countedCurrentIndex = currentIndex + (next ? 1 : -1)

    if (!next) {
      countedScrollValue -= additionalMargin
    }
    if (countedScrollValue < 0) {
      countedScrollValue = 0
    }

    setCurrentIndex(countedCurrentIndex)
    setLeftScrollValue(countedScrollValue)

    replyList.current.scrollLeft = countedScrollValue
  }

  const scrollCounter = () => {
    let sumWidth = 0
    replies.forEach((reply, index) => {
      sumWidth += refsArray[index].current.offsetWidth
    })

    return (replyList.current.offsetWidth) < sumWidth
  }

  const postReply = reply => {
    if (hasPayloadValue(reply.postback.payload, 'updateMessages', 'false')) {
      postMessage(reply)
      return
    }
    _wrapper.current.style.pointerEvents = 'none'

    const hideRepliesTimeout = setTimeout(() => {
      setShowReplies(false)
      postMessage(reply)
    }, 650)

    setShowAnimation(true)
    setHideRepliesTimeout(hideRepliesTimeout)
  }

  return (
    <div
      ref={_wrapper}
      style={wrapperStyles}
      className={isVerticalPosition ? classes.listContainerColumn : classes.listContainer}
    >
      {isLeftArrowNeeded && !isVerticalPosition &&
      <div
        className={classes.prevIcon}
        onClick={prevItem}
      >
        <PrevIcon/>
      </div>}

      {!!replies.length &&
        replies.length > 1 ? (
          <ul
            ref={replyList}
            style={quickRepliesHolderStyles}
            className={isVerticalPosition ? classes.listColumn : classes.list}
          >
            {replies.map((reply, index) => (
              <li
                key={uid(reply)}
                ref={refsArray[index]}
                className={isVerticalPosition ? classes.quickReplyContainerColumn : null}
              >
                {isTextReply(reply)
                  ?
                  <QuickReply
                    reply={reply}
                    color={settings.color}
                    botName={settings.bot_name}
                    postMessage={postReply}
                    isVerticalPosition={isVerticalPosition}
                    isImageLoaded={isImageLoaded}
                    setIsImageLoaded={setIsImageLoaded}
                    isLoadingIframeData={isLoadingIframeData}
                    hasDropdown={hasDropdown}
                    messageId={message.id}
                    setPassedOrSkippedFlowComponentsId={setPassedOrSkippedFlowComponentsId}
                    setSelectedValue={setSelectedValue}
                    hasRating={hasRating}
                    setRating={setRating}
                  />
                  :
                  <GeoReply postMessage={postReply} />
                }
              </li>
            ))}
          </ul>
        ) : (
          <div
            ref={replyList}
            style={quickRepliesHolderStyles}
            className={isVerticalPosition ? classes.listColumn : classes.list}
          >
            {replies.map((reply, index) => (
              <div
                key={uid(reply)}
                ref={refsArray[index]}
                className={isVerticalPosition ? classes.quickReplyContainerColumn : null}
              >
                {isTextReply(reply)
                  ?
                  <QuickReply
                    reply={reply}
                    color={settings.color}
                    botName={settings.bot_name}
                    postMessage={postReply}
                    isVerticalPosition={isVerticalPosition}
                    isImageLoaded={isImageLoaded}
                    setIsImageLoaded={setIsImageLoaded}
                    isLoadingIframeData={isLoadingIframeData}
                    hasDropdown={hasDropdown}
                    messageId={message.id}
                    setPassedOrSkippedFlowComponentsId={setPassedOrSkippedFlowComponentsId}
                    setSelectedValue={setSelectedValue}
                    hasRating={hasRating}
                    setRating={setRating}
                  />
                  :
                  <GeoReply postMessage={postReply} />
                }
              </div>
            ))}
          </div>
        )
      }
      {isRightArrowNeeded && !isVerticalPosition &&
      <div
        className={classNames(classes.nextIcon, 'qr-list-next-btn')}
        onClick={nextItem}
      >
        <NextIcon/>
      </div>}
    </div>
  )
}

QuickReplyList.propTypes = {
  settings: PropTypes.object.isRequired,
  replies: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      payload: PropTypes.string.isRequired,
      content_type: PropTypes.string,
      isFilled: PropTypes.bool
    })
  ),
  postMessage: PropTypes.func.isRequired
}

export default QuickReplyList
